import React, { useState } from 'react';
import { PlaySheet } from '../../components/PlaySheet';
import { PlayPrimaryButton } from '../../components/PlayPrimaryButton';
import { useSetMatchResultMutation, useSetPlayerStatusMutation } from 'store/services/play/playApi';
import { MATCH_RESULTS } from 'constants/play';
import type { MatchState, PlayPlayer, TournamentState } from 'types/interfaces/play';
import { playerName } from 'types/interfaces/play';
import { playErrorMessage } from 'utils/play/playErrors';
import './HostLobby.css';
import './HostRound.css';

/**
 * Decisions 14 and 16: the TO enters or overrides a result (both claims shown
 * while disputed; a TO result clears the dispute) and can drop either player.
 */
export const MatchSheet: React.FC<{ code: string; state: TournamentState; match: MatchState; elimination: boolean; onClose: () => void }> = ({ code, state, match, elimination, onClose }) => {
  const [setResult, { isLoading: saving }] = useSetMatchResultMutation();
  const [setStatus, { isLoading: dropping }] = useSetPlayerStatusMutation();
  const [error, setError] = useState<string | null>(null);
  const options = MATCH_RESULTS.filter(([a, b]) => !(elimination && a === b));
  const current = match.gamesA !== null && match.gamesB !== null ? options.findIndex(([a, b]) => a === match.gamesA && b === match.gamesB) : -1;
  const [pick, setPick] = useState<number>(current);
  const a = match.playerA;
  const b = match.playerB;
  const claimOf = (id: string) => match.reports?.find((r) => r.tournamentPlayerId === id);

  const save = async () => {
    if (pick < 0 || !b) return;
    setError(null);
    try {
      const [gamesA, gamesB] = options[pick];
      await setResult({ matchId: match.id, code, gamesA, gamesB }).unwrap();
      onClose();
    } catch (err) {
      setError(playErrorMessage(err, "Couldn't save the result."));
    }
  };

  const drop = async (playerId: string) => {
    setError(null);
    try {
      await setStatus({ id: state.tournament.id, code, playerId, action: 'DROP' }).unwrap();
      onClose();
    } catch (err) {
      setError(playErrorMessage(err, "Couldn't drop the player."));
    }
  };

  return (
    <PlaySheet ariaLabel={`Table ${match.tableNumber ?? ''}`} onClose={onClose}>
      <p className="play-eyebrow">Table {match.tableNumber ?? '–'}</p>
      <h2 className="play-sheet-title">
        {a.username} vs {b?.username ?? 'Bye'}
      </h2>
      <p className="play-helper">
        {match.status === 'DISPUTED' ? 'Disputed. Your result overrides both reports.' : match.status === 'REPORTED' ? 'Reported by a player. Your result overrides it.' : 'No result yet. Enter one to override player reports.'}
      </p>
      {match.status === 'DISPUTED' && (
        <div className="play-sheet-claims" data-testid="match-sheet-claims">
          {[a, b].filter(Boolean).map((p) => {
            const claim = p ? claimOf(p.tournamentPlayerId) : undefined;
            if (!p || !claim) return null;
            const mine = p.tournamentPlayerId === a.tournamentPlayerId ? [claim.gamesA, claim.gamesB] : [claim.gamesB, claim.gamesA];
            return (
              <p key={p.tournamentPlayerId}>
                {p.username} says {mine[0] > mine[1] ? 'they won' : mine[0] < mine[1] ? 'they lost' : 'it was a draw'} {mine[0]}–{mine[1]}
              </p>
            );
          })}
        </div>
      )}
      {b && (
        <div className="play-sheet-picks" role="radiogroup" aria-label="Result, player A games first">
          {options.map(([ga, gb], i) => (
            <button key={`${ga}-${gb}`} type="button" role="radio" aria-checked={pick === i} className={`play-sheet-pick ${pick === i ? 'play-sheet-pick--active' : ''}`} onClick={() => setPick(i)}>
              <span className="play-sheet-pick__score">
                {ga}–{gb}
              </span>
              <span className="play-sheet-pick__sub">{ga > gb ? 'A wins' : ga < gb ? 'B wins' : 'Draw'}</span>
            </button>
          ))}
        </div>
      )}
      {b && (
        <p className="play-pair-hint">
          A = {a.username}, B = {b.username}
        </p>
      )}
      <div className="play-sheet-actions">
        {b && <PlayPrimaryButton label="Save result" disabled={pick < 0 || saving} loading={saving} onClick={save} />}
        <div className="play-sheet-drops">
          <button type="button" className="play-danger-button" onClick={() => drop(a.tournamentPlayerId)} disabled={dropping || !!a.dropped}>
            Drop {a.username}
          </button>
          {b && (
            <button type="button" className="play-danger-button" onClick={() => drop(b.tournamentPlayerId)} disabled={dropping || !!b.dropped}>
              Drop {b.username}
            </button>
          )}
        </div>
      </div>
      {error && (
        <p className="play-error" role="alert">
          {error}
        </p>
      )}
    </PlaySheet>
  );
};

/** Decision 16: drop or reinstate from the Players list. */
export const PlayerSheet: React.FC<{ code: string; state: TournamentState; player: PlayPlayer; onClose: () => void }> = ({ code, state, player, onClose }) => {
  const [setStatus, { isLoading }] = useSetPlayerStatusMutation();
  const [error, setError] = useState<string | null>(null);
  const name = playerName(player);
  const toggle = async () => {
    setError(null);
    try {
      await setStatus({ id: state.tournament.id, code, playerId: player.id, action: player.dropped ? 'REINSTATE' : 'DROP' }).unwrap();
      onClose();
    } catch (err) {
      setError(playErrorMessage(err));
    }
  };
  return (
    <PlaySheet ariaLabel={name} onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span className="play-avatar-initial">{name.slice(0, 1).toUpperCase()}</span>
        <div>
          <h2 className="play-sheet-title" style={{ margin: 0 }}>
            {name}
          </h2>
          <p className="play-helper" style={{ fontSize: 13 }}>
            {[player.firstName, player.lastName].filter(Boolean).join(' ') || (player.username ? '' : 'Walk-in')}
            {player.firstName || player.lastName ? ' · ' : ''}
            {player.hasDecklist ? player.deckName || 'Decklist in' : 'No decklist'} · {player.record}
          </p>
        </div>
      </div>
      <div className="play-sheet-actions">
        {player.dropped ? (
          <PlayPrimaryButton label="Reinstate player" loading={isLoading} onClick={toggle} />
        ) : (
          <button type="button" className="play-danger-button" onClick={toggle} disabled={isLoading}>
            Drop player
          </button>
        )}
        <button type="button" className="play-secondary-button" onClick={onClose}>
          Close
        </button>
      </div>
      {error && (
        <p className="play-error" role="alert">
          {error}
        </p>
      )}
    </PlaySheet>
  );
};
